/**
 * This file includes code that is a derivative work of the following open source  project:
 *
 * Name: '@okta/auth-js'
 * URL: https://github.com/okta/okta-auth-js
 * License: Apache 2.0 (https://www.apache.org/licenses/LICENSE-2.0)
 *
 * The code as been modified to meet the requirements of this project.
 */

import { SimpleStorage, StorageProvider } from './storage.types';
import { AuthSdkError } from '../lib/auth-sdk-error';

const FALLBACK_STRING = '{}';

/**
 * A wrapper around a specific store entry by key. This allows each key to be
 * treated as an individual store, with its own set, get, and clear methods
 * independent of where the key is actually persisted. The StoredObject handles
 * serialization and deserialization of the stored value.
 *
 * This is used by the StorageManager to provide a consistent interface for
 * interacting with different storage types.
 *
 * @param storage
 * @param storageKey
 */
export class StoredObject<IStorage = unknown> implements StorageProvider<IStorage> {
  constructor(
    private readonly storage: SimpleStorage,
    private readonly storageKey: string,
  ) {}

  /**
   * Updates the entire stored value with a serialized version of the input.
   * @param obj
   * @throws {AuthSdkError} - There is a problem stringifying or saving the object
   */
  setStorage(obj?: unknown): void {
    try {
      const storageString = JSON.stringify(obj ?? FALLBACK_STRING);
      this.storage.setItem(this.storageKey, storageString);
    } catch (e) {
      throw new AuthSdkError(`Unable to set storage: ${this.storageKey}\n`, e);
    }
  }

  /**
   * Returns the entire stored value and attempts to parse it to an object
   * @throws {AuthSdkError} - Unable to parse stored value
   */
  getStorage() {
    try {
      const strValue = this.storage.getItem(this.storageKey) ?? FALLBACK_STRING;
      return typeof strValue === 'string' ? JSON.parse(strValue) : strValue;
    } catch (e) {
      throw new AuthSdkError(`Unable to parse stored object: ${this.storageKey}`, e);
    }
  }

  /**
   * Used for clearing values from the store.
   * If a key is provided, only that key will be removed.
   * If no key is provided, the entire store will be cleared.
   * @param key
   */
  clearStorage(key?: string | undefined): void {
    if (!key) {
      this.storage.removeItem(this.storageKey);
      return;
    }

    const storeObj = this.getStorage();
    delete storeObj[key];
    this.setStorage(storeObj);
  }

  /**
   * Used for updating a single value in the store.
   * @param key
   * @param value
   */
  updateStorage(key: string, value: unknown): void {
    const storeObj = this.getStorage();
    storeObj[key] = value;
    this.setStorage(storeObj);
  }

  /**
   * Compat with SimpleStorage interface
   * @param key
   * @param value
   */
  setItem(key: string, value: string): void {
    return this.updateStorage(key, value);
  }

  /**
   * Compat with SimpleStorage interface
   * @param key
   */
  getItem(key: string) {
    return this.getStorage()?.[key] ?? null;
  }
  /**
   * Compat with SimpleStorage interface
   * @param key
   */
  removeItem(key: string): void {
    return this.clearStorage(key);
  }
}
